import React from "react";
import { Star, ThumbsUp } from "@phosphor-icons/react";

/**
 * Compact chip that surfaces a candidate's aggregated reference-check
 * score on the Kanban card + candidate list. Renders nothing if no
 * references have been submitted yet — designed to slot next to the
 * existing AI-match `ScoreBadge`.
 *
 * Data source: `candidate.reference_summary` — denormalised by the
 * `/api/public/reference/{token}/submit` endpoint. See
 * `backend/routes/reference_checks.py::_refresh_candidate_ref_summary`.
 */
export default function ReferenceScoreChip({ summary, testid }) {
    if (!summary || !summary.count) return null;
    const { count, avg_rating, rehire_yes } = summary;
    const rehirePct = count ? Math.round((rehire_yes / count) * 100) : 0;

    // Color tiers — mirror the AI-match badge language:
    //   ≥ 4.0 avg & 100% rehire  → emerald "strong"
    //   ≥ 3.0                    → amber "OK"
    //   otherwise                → rose "weak"
    let tone = "bg-rose-50 text-rose-800 border-rose-200";
    if ((avg_rating ?? 0) >= 4 && rehirePct === 100) {
        tone = "bg-emerald-50 text-emerald-800 border-emerald-200";
    } else if ((avg_rating ?? 0) >= 3) {
        tone = "bg-amber-50 text-amber-800 border-amber-200";
    }

    return (
        <span
            data-testid={testid || "ref-score-chip"}
            title={`${count} reference${count === 1 ? "" : "s"} · avg ${avg_rating}/5 · ${rehirePct}% would rehire`}
            className={`inline-flex items-center gap-1 border ${tone} px-1.5 py-0.5 text-[10px] font-black tracking-wider whitespace-nowrap`}
        >
            <Star size={9} weight="fill" />
            {avg_rating}
            <span className="text-[8px] opacity-70">/5</span>
            <span className="opacity-40">·</span>
            <ThumbsUp size={8} weight="fill" />
            {rehirePct}%
        </span>
    );
}
